import { contrastRatio } from './color.ts'
import { americaTheme, roleDescriptions, roleNames } from './theme.ts'
import { missionCss, themesCss, tokensCss } from './css.ts'
import { agencies, missionPath, missions, themeFor } from './missions.ts'
import { fontsCss } from './fonts.ts'
import { components } from './components.ts'
import { pageTemplates } from './templates.ts'

export const SYSTEM_VERSION = '1.0.1'
export const SYSTEM_NAME = 'Mission Design System'
export const SITE_URL = 'https://missiondesign.us'

export const sources = {
  uswds: {
    title: 'U.S. Web Design System',
    url: 'https://designsystem.digital.gov/',
  },
  accessibility: { title: 'WCAG 2.1', url: 'https://www.w3.org/TR/WCAG21/' },
  section508: {
    title: 'Revised Section 508 standards',
    url: 'https://www.access-board.gov/ict/',
  },
  plainLanguage: {
    title: 'Federal plain language guidelines',
    url: 'https://digital.gov/guides/plain-language/',
  },
  nds: { title: 'National Design Studio', url: 'https://ndstudio.gov/' },
} as const

export type DesignToken = {
  name: string
  css: string
  value: string
  dark?: string
  role: string
  group: 'color' | 'typography' | 'spacing' | 'shape' | 'elevation' | 'motion' | 'layout'
}

const fontStacks = {
  display: '"Instrument Serif", "Iowan Old Style", Georgia, serif',
  sans: '"Inter Variable", Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
  mono: '"Geist Mono Variable", ui-monospace, SFMono-Regular, Menlo, monospace',
}

const scale = (
  group: DesignToken['group'],
  prefix: string,
  entries: Array<[string, string, string]>,
): DesignToken[] =>
  entries.map(([key, value, role]) => ({
    name: `${prefix.replace(/-/g, '.')}.${key}`,
    css: `--mds-${prefix}-${key}`,
    value,
    role,
    group,
  }))

export const tokens: DesignToken[] = [
  ...roleNames.map(
    (name): DesignToken => ({
      name: `color.${name.replace('-', '.')}`,
      css: `--mds-${name}`,
      value: americaTheme.light[name],
      dark: americaTheme.dark[name],
      role: roleDescriptions[name],
      group: 'color',
    }),
  ),
  {
    name: 'font.display',
    css: '--mds-font-display',
    value: fontStacks.display,
    role: 'Display serif for heroes, page titles and section headings.',
    group: 'typography',
  },
  {
    name: 'font.sans',
    css: '--mds-font-sans',
    value: fontStacks.sans,
    role: 'Body text, interface labels and sans headings.',
    group: 'typography',
  },
  {
    name: 'font.mono',
    css: '--mds-font-mono',
    value: fontStacks.mono,
    role: 'Code, token names and technical labels.',
    group: 'typography',
  },
  ...scale('typography', 'text', [
    ['hero', 'clamp(3rem, 1.6rem + 6.4vw, 7rem)', 'One hero line per page.'],
    ['display', 'clamp(2.5rem, 1.6rem + 3.6vw, 4.5rem)', 'Section headings in the display serif.'],
    ['h1', 'clamp(2.25rem, 1.7rem + 2.4vw, 3.5rem)', 'Page titles.'],
    ['h2', 'clamp(1.75rem, 1.45rem + 1.3vw, 2.5rem)', 'Major sections.'],
    ['h3', 'clamp(1.25rem, 1.15rem + 0.4vw, 1.5rem)', 'Sans subsection headings.'],
    ['lead', 'clamp(1.125rem, 1.05rem + 0.35vw, 1.375rem)', 'Introductory paragraphs.'],
    ['body', '1rem', 'Default reading size.'],
    ['small', '0.875rem', 'Supporting labels.'],
    ['micro', '0.75rem', 'Banner, eyebrows and captions.'],
  ]),
  ...scale('spacing', 'space', [
    ['1', '0.25rem', 'Hairline gaps.'],
    ['2', '0.5rem', 'Icon and label.'],
    ['3', '0.75rem', 'Compact padding.'],
    ['4', '1rem', 'Default gap.'],
    ['6', '1.5rem', 'Card padding.'],
    ['8', '2rem', 'Related groups.'],
    ['12', '3rem', 'Content blocks.'],
    ['16', '4rem', 'Section padding on small screens.'],
    ['24', '6rem', 'Section padding.'],
    ['32', '8rem', 'Hero and feature bands.'],
  ]),
  ...scale('shape', 'radius', [
    ['control', '999px', 'Buttons, chips and the prompt pill.'],
    ['field', '1rem', 'Inputs and selects.'],
    ['card', '1.5rem', 'Cards and panels.'],
    ['hero', '3rem', 'The hero canvas and full-width media.'],
  ]),
  ...scale('elevation', 'shadow', [
    ['1', '0 10px 40px rgb(1 14 36 / 0.08)', 'Raised cards and menus.'],
    ['2', '0 48px 64px rgb(0 0 0 / 0.1)', 'Dialogs and featured media.'],
    ['field', '0 4px 16px rgb(1 14 36 / 0.08)', 'The prompt pill and inputs at rest.'],
  ]),
  ...scale('motion', 'ease', [
    ['out', 'cubic-bezier(0.22, 1, 0.36, 1)', 'Entrances and reveals.'],
    ['drawer', 'cubic-bezier(0.32, 0.72, 0, 1)', 'Sheets and drawers.'],
    ['standard', 'cubic-bezier(0.4, 0, 0.2, 1)', 'State changes.'],
  ]),
  ...scale('motion', 'duration', [
    ['fast', '150ms', 'Hover and press.'],
    ['base', '250ms', 'Toggles and small transitions.'],
    ['slow', '500ms', 'Sheets and reveals.'],
    ['story', '900ms', 'Hero and scroll storytelling.'],
  ]),
  ...scale('layout', 'container', [
    ['site', '87rem', 'Widest content column.'],
    ['text', '42rem', 'Comfortable reading measure.'],
  ]),
]

export { contrastRatio }

export type PatternCategory = 'Operations' | 'Transactions' | 'Intelligence'
export type Pattern = {
  id: string
  title: string
  eyebrow: string
  category: PatternCategory
  summary: string
  useWhen: string
  anatomy: string[]
  requirements: string[]
  acceptance: string[]
  related: string[]
  sourceIds: (keyof typeof sources)[]
}

export const patterns: Pattern[] = [
  {
    id: 'mission-dashboard',
    title: 'Mission dashboard',
    eyebrow: 'See the whole picture',
    category: 'Operations',
    summary:
      'Make the current mission state clear, then put the next useful action within reach.',
    useWhen:
      'People need an overview of a mission, portfolio or operational workload before deciding where to focus.',
    anatomy: [
      'Mission context and reporting period',
      'Priority metrics with units and time frame',
      'Labeled status distribution',
      'Actionable work items with clear destinations',
    ],
    requirements: [
      'Give each metric a meaningful label and time frame.',
      'Pair every status color with a text label.',
      'Offer a text equivalent for every chart.',
      'Make the next action more prominent than supporting information.',
    ],
    acceptance: [
      'A reader can identify the mission and reporting period.',
      'Every chart value is also available as text.',
      'Changing the reporting period updates labels and values together.',
    ],
    related: ['review-queue', 'record-detail'],
    sourceIds: ['uswds', 'accessibility'],
  },
  {
    id: 'review-queue',
    title: 'Review queue',
    eyebrow: 'Move work forward',
    category: 'Operations',
    summary:
      'Help reviewers find, prioritize and act on work without losing their place.',
    useWhen:
      'A team works through applications, cases or requests with explicit states and repeatable review steps.',
    anatomy: [
      'Queue title and result count',
      'Labeled search and status filters',
      'Scannable item summaries',
      'Action and result feedback',
    ],
    requirements: [
      'Expose the current filters and a clear reset action.',
      'Use descriptive item names for row actions.',
      'Announce changes to the result count.',
      'Preserve context after an item changes state.',
    ],
    acceptance: [
      'Search and filters work together.',
      'An empty result includes a recovery action.',
      'Updating a record announces the new state.',
    ],
    related: ['mission-dashboard', 'record-detail', 'empty-state'],
    sourceIds: ['uswds', 'accessibility'],
  },
  {
    id: 'guided-intake',
    title: 'Guided intake',
    eyebrow: 'One clear step at a time',
    category: 'Transactions',
    summary:
      'Turn a complex request into a short sequence people can understand and review.',
    useWhen:
      'A request needs several related pieces of information and benefits from a review step before submission.',
    anatomy: [
      'Purpose and expected effort',
      'Visible progress',
      'Labeled fields with contextual help',
      'Review, edit and confirmation',
    ],
    requirements: [
      'Collect only information needed for the task.',
      'Connect each error to its field and explain the correction.',
      'Keep entered values when moving between steps.',
      'Let people review and edit before a consequential submission.',
    ],
    acceptance: [
      'Required fields explain missing or invalid values.',
      'Back navigation preserves input.',
      'Confirmation names the completed action and next step.',
    ],
    related: ['record-detail', 'empty-state'],
    sourceIds: ['uswds', 'accessibility', 'plainLanguage'],
  },
  {
    id: 'record-detail',
    title: 'Record detail',
    eyebrow: 'Context before action',
    category: 'Transactions',
    summary:
      'Bring a record, its history and the next decision into one dependable workspace.',
    useWhen:
      'People need to understand the state, ownership and history of one case or mission item.',
    anatomy: [
      'Human-readable record title',
      'Current state and owner',
      'Structured facts',
      'Activity history and contextual actions',
    ],
    requirements: [
      'Use semantic description lists for label/value pairs.',
      'Display dates with an explicit time zone when relevant.',
      'Keep read-only context visible during a decision.',
      'Confirm destructive or consequential changes.',
    ],
    acceptance: [
      'The record has a unique descriptive heading.',
      'Status changes update both summary and history.',
      'A user can cancel an action without changing the record.',
    ],
    related: ['review-queue', 'guided-intake'],
    sourceIds: ['uswds', 'accessibility'],
  },
  {
    id: 'cited-answer',
    title: 'Cited answer',
    eyebrow: 'Show the evidence',
    category: 'Intelligence',
    summary:
      'Connect an assisted answer to its sources and keep the human decision explicit.',
    useWhen:
      'An application synthesizes information or proposes a next step that a person needs to evaluate.',
    anatomy: [
      'Clear question and scope',
      'Answer with nearby citations',
      'Source details and boundaries',
      'Human review and feedback',
    ],
    requirements: [
      'Link claims to specific accessible sources.',
      'Distinguish source facts from suggestions.',
      'Explain when the available evidence does not answer the question.',
      'Ask for explicit confirmation before consequential action.',
    ],
    acceptance: [
      'Every factual summary links to supporting evidence.',
      'A reader can inspect source details independently.',
      'Submitting feedback announces the recorded choice.',
    ],
    related: ['record-detail', 'empty-state'],
    sourceIds: ['uswds', 'accessibility', 'plainLanguage'],
  },
  {
    id: 'empty-state',
    title: 'Empty & error states',
    eyebrow: 'A way forward',
    category: 'Operations',
    summary:
      'Explain what happened, preserve useful context and offer a practical next step.',
    useWhen:
      'A view is loading, has no records, has no matches or cannot complete an operation.',
    anatomy: [
      'Plain-language state heading',
      'Brief explanation',
      'Relevant recovery action',
      'Accessible status announcement',
    ],
    requirements: [
      'Separate first-use, no-results, loading and failure states.',
      'Preserve filters and input after a recoverable error.',
      'Use a status region for asynchronous updates.',
      'Describe the recovery action without exposing internal diagnostics.',
    ],
    acceptance: [
      'No-results offers a way to clear the active filter.',
      'A recoverable failure has a retry action.',
      'Loading and completion are announced without moving focus.',
    ],
    related: ['review-queue', 'guided-intake'],
    sourceIds: ['uswds', 'accessibility', 'plainLanguage'],
  },
]

export const agentRules = [
  {
    id: 'mission-first',
    level: 'must',
    title: 'Start with the mission',
    instruction:
      'Identify the user, task, decision and operating context before selecting a pattern.',
  },
  {
    id: 'reuse-patterns',
    level: 'must',
    title: 'Compose from patterns',
    instruction:
      'Read the selected pattern requirements and acceptance checks. Reuse existing components before creating a new one.',
  },
  {
    id: 'semantic-tokens',
    level: 'must',
    title: 'Use semantic tokens',
    instruction:
      'Use the semantic role properties (--mds-ink, --mds-primary, --mds-on-primary and the rest) rather than raw colors, so light, dark and every mission theme work without changes. Pair each fill with its on- role.',
  },
  {
    id: 'mission-theme',
    level: 'must',
    title: 'Theme by mission',
    instruction:
      'Load /mds/tokens.css, /mds/fonts.css and the mission theme from /mds/themes/<id>.css. Set data-theme on <html> from the visitor preference and data-mission to the agency id.',
  },
  {
    id: 'accessible-output',
    level: 'must',
    title: 'Build accessible interactions',
    instruction:
      'Use semantic HTML, visible keyboard focus, explicit labels, text status cues, associated errors and live regions. Support a 320 CSS pixel viewport.',
  },
  {
    id: 'complete-states',
    level: 'must',
    title: 'Include the whole interaction',
    instruction:
      'Implement loading, empty, success and error states when data is involved. Preserve input and context on recovery.',
  },
  {
    id: 'source-evidence',
    level: 'must',
    title: 'Keep evidence reachable',
    instruction:
      'Link factual summaries to the specific source supporting them. Label illustrative data and keep consequential decisions with the user.',
  },
  {
    id: 'operator-identity',
    level: 'must',
    title: 'Use the right identity',
    instruction:
      'Show the official banner, the agency seal and the identifier only on the agency\'s own .gov or .mil site. Elsewhere use the alternate banner wording and an original emblem.',
  },
  {
    id: 'choose-date-input',
    level: 'must',
    title: 'Pick the right date input',
    instruction:
      'Use the memorable date for dates people know without a calendar, such as a date of birth, and the date picker for dates near today. Always accept a typed date.',
  },
  {
    id: 'choose-option-input',
    level: 'must',
    title: 'Pick the right option input',
    instruction:
      'Use radio buttons for five or fewer options, a select for up to about fifteen, and the combo box for longer lists such as states or agencies.',
  },
  {
    id: 'tooltips-supplement',
    level: 'must',
    title: 'Keep tooltips supplementary',
    instruction:
      'A tooltip may name an icon-only control or add a brief detail. Never put instructions, errors or required information in one.',
  },
  {
    id: 'tables-compare',
    level: 'must',
    title: 'Use tables to compare',
    instruction:
      'Use the data table when people compare records across the same fields: give it a caption and keep cells labeled for the stacked layout. Use cards or a list when each item is read on its own.',
  },
  {
    id: 'verify-work',
    level: 'must',
    title: 'Verify the experience',
    instruction:
      'Run type checks, lint, component tests, route-level axe, browser interaction tests and 320px reflow checks. Record manual accessibility review separately.',
  },
] as const

export const agentInstructions = `# Mission Design System\n\nVersion: ${SYSTEM_VERSION}\n\nMDS is the design system for United States government websites, built by MetaPhase.\n\n## Read first\n\n1. Load /mds/manifest.json for versioned asset paths.\n2. Read /mds/tokens.json and link /mds/tokens.css and /mds/fonts.css.\n3. Choose a mission theme from /mds/themes/index.json and link its CSS.\n4. Select components from /mds/components.json and a pattern from /mds/patterns.json.\n5. Apply /mds/rules.json and verify the pattern acceptance checks.\n\n## Rules\n\n${agentRules.map((rule) => `- ${rule.title}: ${rule.instruction}`).join('\n')}\n\n## Delivery\n\nExplain the selected pattern, changed files, test results and remaining human review. MDS supports accessibility implementation; each application needs its own review and authorization.\n`

export const machineAssets = [
  {
    path: '/mds/manifest.json',
    name: 'manifest.json',
    kind: 'JSON',
    description: 'Version and asset discovery',
  },
  {
    path: '/mds/tokens.json',
    name: 'tokens.json',
    kind: 'JSON',
    description: 'Semantic design tokens and contrast pairs',
  },
  {
    path: '/mds/tokens.css',
    name: 'tokens.css',
    kind: 'CSS',
    description: 'Ready-to-use custom properties',
  },
  {
    path: '/mds/fonts.css',
    name: 'fonts.css',
    kind: 'CSS',
    description: 'Typeface options as attribute switches',
  },
  {
    path: '/mds/themes.css',
    name: 'themes.css',
    kind: 'CSS',
    description: 'Every mission theme, scoped by data-mission',
  },
  {
    path: '/mds/themes/index.json',
    name: 'themes/index.json',
    kind: 'JSON',
    description: 'Mission theme index with per-agency CSS and JSON',
  },
  {
    path: '/mds/mds.css',
    name: 'mds.css',
    kind: 'CSS',
    description: 'One stylesheet for a page: tokens, typefaces, themes and components',
  },
  {
    path: '/mds/templates.json',
    name: 'templates.json',
    kind: 'JSON',
    description: 'Page templates with their HTML and React source',
  },
  {
    path: '/mds/tokens/{mission}/light.tokens.json',
    name: 'tokens/{mission}/light.tokens.json',
    kind: 'JSON',
    description: 'A mission theme as W3C design tokens, one file per mode (light and dark), for Figma variables',
  },
  {
    path: '/mds/tokens/{mission}/penpot.tokens.json',
    name: 'tokens/{mission}/penpot.tokens.json',
    kind: 'JSON',
    description: 'A mission theme as Penpot token sets and themes',
  },
  {
    path: '/mds/swatches/{mission}.ase',
    name: 'swatches/{mission}.ase',
    kind: 'ASE',
    description: 'A mission theme as Adobe swatches, light and dark',
  },
  {
    path: '/mds/sketch/{mission}.sketch',
    name: 'sketch/{mission}.sketch',
    kind: 'Sketch',
    description: 'A mission theme as a Sketch library of Color Variables and text styles',
  },
  {
    path: '/mds/canva/{mission}.zip',
    name: 'canva/{mission}.zip',
    kind: 'ZIP',
    description: 'A mission brand pack for Canva: palette, mark and steps',
  },
  {
    path: '/mds/components.css',
    name: 'components.css',
    kind: 'CSS',
    description: 'Component styles: the mds-* classes',
  },
  {
    path: '/mds/components.json',
    name: 'components.json',
    kind: 'JSON',
    description: 'Component anatomy, accessibility notes and USWDS equivalents',
  },
  {
    path: '/mds/patterns.json',
    name: 'patterns.json',
    kind: 'JSON',
    description: 'Pattern anatomy and acceptance checks',
  },
  {
    path: '/mds/rules.json',
    name: 'rules.json',
    kind: 'JSON',
    description: 'Machine-readable implementation rules',
  },
  {
    path: '/mds/agents.md',
    name: 'agents.md',
    kind: 'Markdown',
    description: 'Portable agent instructions',
  },
] as const

export const documentationPages = [
  {
    path: '/getting-started',
    title: 'Get Started',
    description:
      'Choose a mission pattern, apply the tokens and verify your application.',
  },
  {
    path: '/design-tools',
    title: 'Design tools',
    description: 'A Figma plugin and variables, Adobe swatches, Sketch, Penpot and Canva files for every mission theme.',
  },
  {
    path: '/foundations',
    title: 'Foundations',
    description: 'Color, typography, spacing, accessibility and design tokens.',
  },
  {
    path: '/foundations/color',
    title: 'Color',
    description: 'Semantic color roles, mission palettes, light and dark themes, and contrast.',
  },
  {
    path: '/foundations/typography',
    title: 'Typography',
    description: 'Display serif, body sans and mono typefaces, the type scale and font options.',
  },
  {
    path: '/foundations/layout',
    title: 'Layout',
    description: 'Spacing scale, density, page and reading widths, gutters, breakpoints, even grids, reflow and the sticky header.',
  },
  {
    path: '/foundations/shape',
    title: 'Shape',
    description: 'Corner radius, elevation, borders and dividers, the focus ring and surfaces.',
  },
  {
    path: '/foundations/motion',
    title: 'Motion',
    description: 'Easing curves, durations and reduced motion.',
  },
  {
    path: '/foundations/identity',
    title: 'Identity',
    description: 'Flags, seals and emblems, the banner and the identifier.',
  },
  {
    path: '/components',
    title: 'Components',
    description: 'Accessible, themeable components with USWDS equivalents.',
  },
  {
    path: '/templates',
    title: 'Templates',
    description: 'Whole pages built from MDS components: agency home, service landing, multi-step form and search results.',
  },
  {
    path: '/missions',
    title: 'Missions',
    description: 'Pregenerated agency themes with marks, palettes and downloads.',
  },
  {
    path: '/patterns',
    title: 'Pattern library',
    description:
      'Reusable mission dashboards, review queues, intake and cited answers.',
  },
  {
    path: '/agents',
    title: 'Agents',
    description:
      'Machine-readable tokens, patterns, rules and implementation instructions.',
  },
  {
    path: '/about',
    title: 'About MDS',
    description: 'Mission Design System, MPworks and MetaPhase.',
  },
  {
    path: '/accessibility',
    title: 'Accessibility',
    description:
      'Accessibility targets, testing approach and reporting barriers.',
  },
  {
    path: '/credits',
    title: 'Credits',
    description:
      'The National Design Studio, the U.S. Web Design System, and the photographs, seals and typefaces MDS uses.',
  },
  {
    path: '/license',
    title: 'License',
    description: 'The Protoware License under which MDS is offered to government.',
  },
] as const

export const siteRoutes = [
  '/',
  ...documentationPages.map((page) => page.path),
  ...patterns.map((pattern) => `/patterns/${pattern.id}`),
  ...components.map((component) => `/components/${component.id}`),
  ...pageTemplates.map((template) => `/templates/${template.id}`),
  ...missions.map((mission) => `/missions/${mission.id}`),
  // Each agency's home page, at its own address such as /cbp.
  ...agencies.map(missionPath),
  '/privacy',
  '/disclaimer',
  '/search',
  '/missing-page',
]

export function searchDocumentation(query: string) {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const entries = [
    ...documentationPages,
    ...patterns.map((pattern) => ({
      path: `/patterns/${pattern.id}`,
      title: pattern.title,
      description: `${pattern.summary} ${pattern.category}`,
    })),
    ...components.map((component) => ({
      path: `/components/${component.id}`,
      title: component.name,
      description: `${component.summary} ${component.category} ${component.uswds.join(' ')}`,
    })),
    ...pageTemplates.map((template) => ({
      path: `/templates/${template.id}`,
      title: template.name,
      description: `${template.summary} template`,
    })),
    ...missions.map((mission) => ({
      path: `/missions/${mission.id}`,
      title: mission.name,
      description: `${mission.short} mission theme ${mission.kind}`,
    })),
  ]
  return entries.filter((entry) =>
    words.every((word) =>
      `${entry.title} ${entry.description}`.toLowerCase().includes(word),
    ),
  )
}

/** The machine-readable files under /mds/. Component styles are read from src/styles by the caller. */
export function machineFiles(componentStyles = ''): Record<string, string> {
  const json = (value: unknown) => `${JSON.stringify(value, null, 2)}\n`
  const banner = `Mission Design System ${SYSTEM_VERSION}. Generated from src/content; do not edit.`
  const manifest = {
    name: SYSTEM_NAME,
    version: SYSTEM_VERSION,
    operator: 'MetaPhase',
    assets: machineAssets.map(({ path, kind }) => ({
      path,
      format: kind.toLowerCase(),
    })),
    sources,
  }
  return {
    'manifest.json': json(manifest),
    'tokens.json': json({
      name: SYSTEM_NAME,
      version: SYSTEM_VERSION,
      tokens,
    }),
    'tokens.css': tokensCss(tokens, `${banner} Core tokens and the default American theme.`, americaTheme),
    'fonts.css': fontsCss(`${banner} Typeface switches for data-font-display and data-font-sans.`),
    'themes.css': themesCss(`${banner} Every mission theme, scoped by data-mission.`),
    'themes/index.json': json({
      name: SYSTEM_NAME,
      version: SYSTEM_VERSION,
      themes: missions.map(({ id, name, short, kind }) => ({
        id,
        name,
        short,
        kind,
        css: `/mds/themes/${id}.css`,
        json: `/mds/themes/${id}.json`,
      })),
    }),
    ...Object.fromEntries(
      missions.flatMap((mission) => [
        [`themes/${mission.id}.css`, missionCss(mission, `${banner} ${mission.name} theme.`)],
        [
          `themes/${mission.id}.json`,
          json({
            name: `${SYSTEM_NAME}: ${mission.name}`,
            version: SYSTEM_VERSION,
            mission,
            roles: themeFor(mission),
          }),
        ],
      ]),
    ),
    'components.css': `/* ${banner} Component styles: the mds-* classes. */\n${componentStyles}`,
    'mds.css': `/* ${banner} Everything a page needs: tokens, typeface switches, mission themes and components. */\n@import './tokens.css';\n@import './fonts.css';\n@import './themes.css';\n@import './components.css';\n`,
    'components.json': json({
      name: SYSTEM_NAME,
      version: SYSTEM_VERSION,
      components,
    }),
    'templates.json': json({
      name: SYSTEM_NAME,
      version: SYSTEM_VERSION,
      templates: pageTemplates.map((template) => ({
        ...template,
        html: `/mds/templates/${template.id}.html`,
        react: `/mds/templates/react/${template.source}`,
        shared: ['/mds/templates/react/TemplateChrome.tsx', '/mds/templates/react/types.ts'],
      })),
    }),
    'patterns.json': json({
      name: SYSTEM_NAME,
      version: SYSTEM_VERSION,
      patterns,
      sources,
    }),
    'rules.json': json({
      name: SYSTEM_NAME,
      version: SYSTEM_VERSION,
      rules: agentRules,
    }),
    'agents.md': agentInstructions,
  }
}

/** A plain-text index for language models, served at /llms.txt. */
export function llmsText(): string {
  const pages = documentationPages.map((page) => `- [${page.title}](${page.path}): ${page.description}`)
  const assets = machineAssets.map((asset) => `- [${asset.name}](${asset.path}): ${asset.description}`)
  return [
    `# ${SYSTEM_NAME}`,
    '',
    '> The design system for United States government websites: foundations, components, templates, patterns and a theme for every agency, built by MetaPhase.',
    '',
    `Version ${SYSTEM_VERSION}, at ${SITE_URL}. Start with /mds/agents.md, then /mds/manifest.json. The whole reference in one file is /llms-full.txt.`,
    '',
    '## Guidance',
    '',
    ...pages,
    '',
    '## Templates',
    '',
    ...pageTemplates.map((template) => `- [${template.name}](/mds/templates/${template.id}.html): ${template.summary}`),
    '',
    '## Machine-readable files',
    '',
    ...assets,
    '',
    '## Packages',
    '',
    '- missiondesign: stylesheets, typefaces, mission themes and the JSON data',
    '- missiondesign-react: React components',
    '',
  ].join('\n')
}

/**
 * The whole system as one plain-text file for language models, served at
 * /llms-full.txt: how to use it, the tokens and color roles, every component,
 * pattern and template, and every mission's colors. Built from the same
 * records as the site, so it changes when they do.
 */
export function llmsFullText(): string {
  const list = (items: readonly string[]) => items.map((item) => `- ${item}`)
  const groups = ['color', 'typography', 'spacing', 'shape', 'elevation', 'motion', 'layout'] as const
  return [
    `# ${SYSTEM_NAME}: full reference`,
    '',
    '> The design system for United States government websites: foundations, components, templates, patterns and a theme for every agency, built by MetaPhase.',
    '',
    `Version ${SYSTEM_VERSION}, at ${SITE_URL}. The short index is /llms.txt; the machine-readable files are listed in /mds/manifest.json.`,
    '',
    '## Using MDS',
    '',
    '- Install `missiondesign` from npm and import `missiondesign/mds.css`, or link /mds/tokens.css, /mds/fonts.css, /mds/themes.css and /mds/components.css in that order.',
    '- React components are in `missiondesign-react`. Start a new site with `npx missiondesign-create my-site`.',
    '- Set `data-theme="light"` or `"dark"` and `data-mission="<id>"` on `<html>` to choose the mode and the agency theme. Components read semantic roles, never raw colors.',
    `- Each agency's home page is at its abbreviation, such as ${SITE_URL}/cbp.`,
    '',
    '## Color roles',
    '',
    ...roleNames.map((role) => `- \`--mds-${role}\`: ${roleDescriptions[role]}`),
    '',
    '## Tokens',
    '',
    ...groups.flatMap((group) => [
      `### ${group[0].toUpperCase()}${group.slice(1)}`,
      '',
      ...tokens.filter((token) => token.group === group).map((token) => `- \`${token.css}\`: ${token.value}${token.dark ? ` (dark: ${token.dark})` : ''}. ${token.role}`),
      '',
    ]),
    '## Components',
    '',
    ...components.flatMap((component) => [
      `### ${component.name}`,
      '',
      `${component.summary} (${component.category}; ${SITE_URL}/components/${component.id})`,
      '',
      'Use when:',
      ...list(component.useWhen),
      '',
      'Anatomy:',
      ...list(component.anatomy),
      '',
      'Accessibility:',
      ...list(component.accessibility),
      '',
      ...(component.uswds.length ? [`Replaces USWDS: ${component.uswds.join(', ')}.`, ''] : []),
      `Classes: ${component.classes.map((name) => `\`${name}\``).join(', ')}.`,
      '',
      '```',
      component.example,
      '```',
      '',
    ]),
    '## Patterns',
    '',
    ...patterns.flatMap((pattern) => [
      `### ${pattern.title}`,
      '',
      `${pattern.summary} (${pattern.category}; ${SITE_URL}/patterns/${pattern.id})`,
      '',
      `Use when: ${pattern.useWhen}`,
      '',
      'Anatomy:',
      ...list(pattern.anatomy),
      '',
      'Requirements:',
      ...list(pattern.requirements),
      '',
      'Acceptance:',
      ...list(pattern.acceptance),
      '',
    ]),
    '## Page templates',
    '',
    ...pageTemplates.flatMap((template) => [
      `### ${template.name}`,
      '',
      `${template.summary} HTML at /mds/templates/${template.id}.html.`,
      '',
      'Anatomy:',
      ...list(template.anatomy),
      '',
      `Components: ${template.components.join(', ')}.`,
      '',
    ]),
    '## Missions',
    '',
    'Each line: name, abbreviation, `data-mission` id, home page, and the primary, secondary and accent colors its theme derives from.',
    '',
    ...missions.map(
      (mission) =>
        `- ${mission.name} (${mission.short}): \`${mission.id}\`, ${SITE_URL}${missionPath(mission)}, ${mission.palette.primary} ${mission.palette.secondary} ${mission.palette.accent}`,
    ),
    '',
  ].join('\n')
}

/** Every public route, for search engines. */
export function sitemapXml(): string {
  const urls = siteRoutes
    .filter((route) => route !== '/missing-page' && route !== '/search')
    .map((route) => `  <url><loc>${SITE_URL}${route}</loc></url>`)
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls.join('\n')}\n</urlset>\n`
}

export function robotsTxt(): string {
  return `User-agent: *\nAllow: /\n\nSitemap: ${SITE_URL}/sitemap.xml\n`
}
